import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { 
  Plus, 
  Trash2, 
  Edit2,
  Users,
  Sparkles,
  Save,
  X,
  Tags,
  ChevronDown
} from "lucide-react";
import { toast } from "@/components/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { safeLocalStorage } from "@/utils/safeStorage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = safeLocalStorage.getItem('auth_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// 47 Telegram Official Categories
const TELEGRAM_CATEGORIES = [
  { id: 'news_media', name_en: 'News and Media', name_ar: 'الأخبار والإعلام' },
  { id: 'blogs', name_en: 'Blogs', name_ar: 'المدونات' },
  { id: 'humor_entertainment', name_en: 'Humor and Entertainment', name_ar: 'الفكاهة والترفيه' },
  { id: 'technologies', name_en: 'Technologies', name_ar: 'التكنولوجيا' },
  { id: 'economics', name_en: 'Economics', name_ar: 'الاقتصاد' },
  { id: 'business_startups', name_en: 'Business and Startups', name_ar: 'الأعمال والشركات الناشئة' },
  { id: 'cryptocurrencies', name_en: 'Cryptocurrencies', name_ar: 'العملات الرقمية' },
  { id: 'travel', name_en: 'Travel', name_ar: 'السفر والسياحة' },
  { id: 'marketing_pr', name_en: 'Marketing, PR, Advertising', name_ar: 'التسويق والإعلان' },
  { id: 'psychology', name_en: 'Psychology', name_ar: 'علم النفس' },
  { id: 'design', name_en: 'Design', name_ar: 'التصميم' },
  { id: 'politics', name_en: 'Politics', name_ar: 'السياسة' },
  { id: 'art', name_en: 'Art', name_ar: 'الفن' },
  { id: 'law', name_en: 'Law', name_ar: 'القانون' },
  { id: 'education', name_en: 'Education', name_ar: 'التعليم' },
  { id: 'books', name_en: 'Books', name_ar: 'الكتب' },
  { id: 'linguistics', name_en: 'Linguistics', name_ar: 'اللغويات' },
  { id: 'career', name_en: 'Career', name_ar: 'الوظائف والمهن' },
  { id: 'edutainment', name_en: 'Edutainment', name_ar: 'الترفيه التعليمي' },
  { id: 'courses_guides', name_en: 'Courses and Guides', name_ar: 'الدورات والأدلة' },
  { id: 'sport', name_en: 'Sport', name_ar: 'الرياضة' },
  { id: 'fashion_beauty', name_en: 'Fashion and Beauty', name_ar: 'الموضة والجمال' },
  { id: 'medicine', name_en: 'Medicine', name_ar: 'الطب' },
  { id: 'health_fitness', name_en: 'Health and Fitness', name_ar: 'الصحة واللياقة' },
  { id: 'pictures_photos', name_en: 'Pictures and Photos', name_ar: 'الصور والتصوير' },
  { id: 'software_apps', name_en: 'Software & Applications', name_ar: 'البرامج والتطبيقات' },
  { id: 'video_films', name_en: 'Video and Films', name_ar: 'الفيديو والأفلام' },
  { id: 'music', name_en: 'Music', name_ar: 'الموسيقى' },
  { id: 'games', name_en: 'Games', name_ar: 'الألعاب' },
  { id: 'food_cooking', name_en: 'Food and Cooking', name_ar: 'الطعام والطبخ' },
  { id: 'quotes', name_en: 'Quotes', name_ar: 'الاقتباسات' },
  { id: 'handiwork', name_en: 'Handiwork', name_ar: 'الأعمال اليدوية' },
  { id: 'family_children', name_en: 'Family & Children', name_ar: 'الأسرة والأطفال' },
  { id: 'nature', name_en: 'Nature', name_ar: 'الطبيعة' },
  { id: 'interior_construction', name_en: 'Interior and Construction', name_ar: 'الديكور والبناء' },
  { id: 'telegram', name_en: 'Telegram', name_ar: 'تيليجرام' },
  { id: 'instagram', name_en: 'Instagram', name_ar: 'انستجرام' },
  { id: 'sales', name_en: 'Sales', name_ar: 'المبيعات' },
  { id: 'transport', name_en: 'Transport', name_ar: 'النقل والمواصلات' },
  { id: 'religion', name_en: 'Religion', name_ar: 'الدين' },
  { id: 'esoterics', name_en: 'Esoterics', name_ar: 'الروحانيات' },
  { id: 'real_estate', name_en: 'Real Estate', name_ar: 'العقارات' },
  { id: 'automotive', name_en: 'Automotive', name_ar: 'السيارات' },
  { id: 'pets_animals', name_en: 'Pets & Animals', name_ar: 'الحيوانات الأليفة' },
  { id: 'science', name_en: 'Science', name_ar: 'العلوم' },
  { id: 'history', name_en: 'History', name_ar: 'التاريخ' },
  { id: 'other', name_en: 'Other', name_ar: 'أخرى' }
];

// Generate color based on index - 47 unique colors
const generateCategoryColor = (index) => {
  const colors = [
    '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', // 1-5
    '#DDA0DD', '#98D8C8', '#F7DC6F', '#BB8FCE', '#85C1E9', // 6-10
    '#F8B500', '#00CED1', '#FF69B4', '#32CD32', '#FFD700', // 11-15
    '#FF4500', '#1E90FF', '#00FA9A', '#FF1493', '#00BFFF', // 16-20
    '#ADFF2F', '#FF6347', '#7B68EE', '#00FF7F', '#DC143C', // 21-25
    '#00FFFF', '#FF00FF', '#FFFF00', '#FF8C00', '#9370DB', // 26-30
    '#3CB371', '#FA8072', '#87CEEB', '#DEB887', '#5F9EA0', // 31-35
    '#D2691E', '#6495ED', '#FFC0CB', '#B0E0E6', '#CD853F', // 36-40
    '#48D1CC', '#C71585', '#40E0D0', '#EE82EE', '#F5DEB3', // 41-45
    '#9ACD32', '#E6E6FA'                                   // 46-47
  ];
  return colors[index % colors.length];
};

const CategoryCard = ({ category, onEdit, onDelete, t, i18n }) => {
  const currentLang = i18n?.language || 'ar';
  // Display name based on current language
  const displayName = currentLang === 'en' 
    ? (category.name_en || category.name) 
    : (category.name_ar || category.name);
  const displayDescription = currentLang === 'en'
    ? (category.description_en || category.description)
    : (category.description_ar || category.description);
  const displayKeywords = currentLang === 'en'
    ? (category.keywords_en?.length > 0 ? category.keywords_en : category.keywords)
    : (category.keywords_ar?.length > 0 ? category.keywords_ar : category.keywords);

  return (
    <div 
      data-testid={`category-card-${category.id}`}
      className="glass rounded-xl p-6 card-hover"
      style={{ borderColor: `${category.color}30` }}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div 
            className="w-4 h-4 rounded-full"
            style={{ background: category.color, boxShadow: `0 0 10px ${category.color}` }}
          ></div>
          <h3 className="text-lg font-semibold">{displayName}</h3>
        </div>
        <div className="flex gap-1">
          <Button 
            data-testid={`edit-category-${category.id}`}
            variant="ghost" 
            size="icon"
            onClick={() => onEdit(category)}
          >
            <Edit2 size={16} />
          </Button>
          <Button 
            data-testid={`delete-category-${category.id}`}
            variant="ghost" 
            size="icon"
            onClick={() => onDelete(category.id)}
          >
            <Trash2 size={16} className="text-red-400" />
          </Button>
        </div>
      </div>

      {displayDescription && (
        <p className="text-sm text-neutral-500 mb-4">{displayDescription}</p>
      )}

      {/* Keywords */}
      <div className="mb-4">
        <p className="text-xs text-neutral-500 mb-2">{t('categories.keywords')}:</p>
        <div className="flex flex-wrap gap-1">
          {displayKeywords?.length > 0 ? (
            displayKeywords.slice(0, 5).map((kw, idx) => (
              <span 
                key={idx} 
                className="text-xs px-2 py-1 rounded-full"
                style={{ background: `${category.color}20`, color: category.color }}
              >
                {kw}
              </span>
            ))
          ) : (
            <span className="text-xs text-neutral-600">{t('categories.noKeywords')}</span>
          )}
          {displayKeywords?.length > 5 && (
            <span className="text-xs text-neutral-500">+{displayKeywords.length - 5}</span>
          )}
        </div>
      </div>

      {/* Members count */}
      <div className="flex items-center gap-2 text-sm">
        <Users size={14} className="text-neutral-500" />
        <span className="font-mono" style={{ color: category.color }}>
          {category.members_count || 0}
        </span>
        <span className="text-neutral-500">{t('categories.member')}</span>
      </div>
    </div>
  );
};

const CategoryForm = ({ category, onSave, onClose, existingCategories, t, i18n }) => {
  const isArabic = i18n?.language === 'ar';
  
  const [selectedCategory, setSelectedCategory] = useState(category?.telegram_category_id || '');
  const [form, setForm] = useState({
    name: category?.name || '',
    name_en: category?.name_en || '',
    name_ar: category?.name_ar || '',
    keywords: category?.keywords?.join(', ') || '',
    keywords_en: category?.keywords_en?.join(', ') || '',
    keywords_ar: category?.keywords_ar?.join(', ') || '',
    description: category?.description || '',
    description_en: category?.description_en || '',
    description_ar: category?.description_ar || '',
    color: category?.color || '#00F0FF',
    telegram_category_id: category?.telegram_category_id || ''
  });

  // Filter out already used categories
  const usedCategoryIds = existingCategories
    .filter(c => c.id !== category?.id)
    .map(c => c.telegram_category_id)
    .filter(Boolean);
  
  const availableCategories = TELEGRAM_CATEGORIES.filter(
    cat => !usedCategoryIds.includes(cat.id)
  );

  // When selecting a category from dropdown, auto-fill names and color
  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    const selected = TELEGRAM_CATEGORIES.find(c => c.id === categoryId);
    if (selected) {
      const colorIndex = TELEGRAM_CATEGORIES.findIndex(c => c.id === categoryId);
      setForm({
        ...form,
        name: selected.name_en,
        name_en: selected.name_en,
        name_ar: selected.name_ar,
        color: generateCategoryColor(colorIndex),
        telegram_category_id: categoryId
      });
    }
  };

  const handleSubmit = () => {
    if (!selectedCategory && !category) {
      toast.error(isArabic ? 'يرجى اختيار فئة' : 'Please select a category');
      return;
    }

    // Get the name based on current language
    const currentName = isArabic ? form.name_ar : form.name_en;
    const currentKeywords = isArabic ? form.keywords_ar : form.keywords_en;
    const currentDescription = isArabic ? form.description_ar : form.description_en;
    
    if (!currentName?.trim()) {
      toast.error(t('errors.required'));
      return;
    }

    // Use the current language value as fallback name
    const fallbackName = currentName.trim();

    onSave({
      name: fallbackName,
      name_en: form.name_en?.trim() || fallbackName,
      name_ar: form.name_ar?.trim() || fallbackName,
      keywords: currentKeywords.split(',').map(k => k.trim()).filter(Boolean),
      keywords_en: form.keywords_en ? form.keywords_en.split(',').map(k => k.trim()).filter(Boolean) : [],
      keywords_ar: form.keywords_ar ? form.keywords_ar.split(',').map(k => k.trim()).filter(Boolean) : [],
      description: currentDescription?.trim() || null,
      description_en: form.description_en?.trim() || null,
      description_ar: form.description_ar?.trim() || null,
      color: form.color,
      telegram_category_id: form.telegram_category_id
    });
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent 
        className="glass sm:max-w-lg"
        style={{
          border: '2px solid #FFFFFF',
          borderRadius: '16px',
          boxShadow: '0 0 20px rgba(255, 255, 255, 0.3)'
        }}
      >
        <DialogHeader>
          <DialogTitle>{category ? t('categories.editCategory') : t('categories.addCategory')}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          {/* Category Selection Dropdown */}
          {!category && (
            <div>
              <label className="text-sm font-bold text-white block mb-2">
                {isArabic ? 'اختر الفئة من تيليجرام *' : 'Select Telegram Category *'}
              </label>
              <Select value={selectedCategory} onValueChange={handleCategorySelect}>
                <SelectTrigger 
                  data-testid="telegram-category-select"
                  className="w-full bg-purple-600 hover:bg-purple-700 border-purple-500 text-white font-medium [&>span]:text-white [&>span]:opacity-100 [&>svg]:text-white [&>svg]:opacity-100"
                >
                  <SelectValue 
                    placeholder={isArabic ? 'اختر فئة...' : 'Select a category...'} 
                    className="text-white"
                  />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {availableCategories.map((cat, index) => {
                    const originalIndex = TELEGRAM_CATEGORIES.findIndex(c => c.id === cat.id);
                    return (
                      <SelectItem key={cat.id} value={cat.id}>
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: generateCategoryColor(originalIndex) }}
                          ></div>
                          <span>{isArabic ? cat.name_ar : cat.name_en}</span>
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <p className="text-xs text-neutral-500 mt-1">
                {isArabic 
                  ? `${availableCategories.length} فئة متاحة من أصل ${TELEGRAM_CATEGORIES.length}`
                  : `${availableCategories.length} of ${TELEGRAM_CATEGORIES.length} categories available`
                }
              </p>
            </div>
          )}

          {/* Show selected category info */}
          {(selectedCategory || category) && (
            <>
              {/* Category Name Display */}
              <div className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: `${form.color}20` }}>
                <div 
                  className="w-6 h-6 rounded-full flex-shrink-0"
                  style={{ backgroundColor: form.color, boxShadow: `0 0 10px ${form.color}` }}
                ></div>
                <div>
                  <p className="font-bold text-white">{isArabic ? form.name_ar : form.name_en}</p>
                  <p className="text-xs text-neutral-400">{isArabic ? form.name_en : form.name_ar}</p>
                </div>
              </div>

              {/* Keywords - Single field based on language */}
              <div>
                <label className="text-sm text-neutral-400 block mb-2">
                  {t('categories.keywordsLabel')}
                </label>
                <Textarea
                  data-testid="category-keywords-input"
                  placeholder={isArabic ? "برمجة، تطوير، كود" : "programming, development, code"}
                  value={isArabic ? form.keywords_ar : form.keywords_en}
                  onChange={(e) => setForm({ 
                    ...form, 
                    [isArabic ? 'keywords_ar' : 'keywords_en']: e.target.value 
                  })}
                  rows={3}
                  dir={isArabic ? "rtl" : "ltr"}
                />
                <p className="text-xs text-neutral-500 mt-1">
                  {isArabic ? 'الكلمات المفتاحية تساعد في تصنيف الأعضاء تلقائياً' : 'Keywords help auto-classify members'}
                </p>
              </div>

              {/* Description - Single field based on language */}
              <div>
                <label className="text-sm text-neutral-400 block mb-2">
                  {t('categories.description')}
                </label>
                <Input
                  data-testid="category-description-input"
                  placeholder={isArabic ? "وصف مختصر للفئة" : "Short description"}
                  value={isArabic ? form.description_ar : form.description_en}
                  onChange={(e) => setForm({ 
                    ...form, 
                    [isArabic ? 'description_ar' : 'description_en']: e.target.value 
                  })}
                  dir={isArabic ? "rtl" : "ltr"}
                />
              </div>

              {/* Color Display */}
              <div>
                <label className="text-sm text-neutral-400 block mb-2">{t('categories.color')}</label>
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-full"
                    style={{ backgroundColor: form.color, boxShadow: `0 0 15px ${form.color}` }}
                  ></div>
                  <span className="text-sm text-neutral-400">
                    {isArabic ? 'اللون يُحدد تلقائياً لكل فئة' : 'Color is auto-assigned for each category'}
                  </span>
                </div>
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button 
            data-testid="save-category-btn" 
            onClick={handleSubmit} 
            className="btn-primary"
            disabled={!selectedCategory && !category}
          >
            <Save size={16} className={isArabic ? "ml-2" : "mr-2"} />
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [classifying, setClassifying] = useState(false);
  const { t, i18n } = useTranslation();
  const isArabic = i18n?.language === 'ar';

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, { headers: getAuthHeaders() });
      setCategories(response.data);
    } catch (error) {
      toast.error(t('errors.serverError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleSave = async (data) => {
    try {
      if (editingCategory) {
        await axios.put(`${API}/categories/${editingCategory.id}`, data, { headers: getAuthHeaders() });
        toast.success(t('success.updated'));
      } else {
        await axios.post(`${API}/categories`, data, { headers: getAuthHeaders() });
        toast.success(t('success.created'));
      }
      setShowForm(false);
      setEditingCategory(null);
      fetchCategories();
    } catch (error) {
      toast.error(t('errors.serverError'));
    }
  };

  const handleDelete = async (categoryId) => {
    if (!window.confirm(t('categories.confirmDelete'))) return;

    try {
      await axios.delete(`${API}/categories/${categoryId}`, { headers: getAuthHeaders() });
      toast.success(t('success.deleted'));
      fetchCategories();
    } catch (error) {
      toast.error(t('errors.serverError'));
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setShowForm(true);
  };

  const handleClassifyAll = async () => {
    try {
      setClassifying(true);
      await axios.post(`${API}/classify`, { all_members: true }, { headers: getAuthHeaders() });
      toast.success(t('categories.classifying'));
    } catch (error) {
      toast.error(t('errors.serverError'));
    } finally {
      setClassifying(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="categories-page" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-wide">{t('categories.title')}</h1>
          <p className="text-neutral-500 mt-1">{t('categories.subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <Button 
            data-testid="classify-all-btn"
            onClick={handleClassifyAll}
            disabled={classifying}
            className="btn-secondary"
          >
            <Sparkles size={18} className="ml-2" />
            {classifying ? t('categories.classifying') : t('categories.smartClassification')}
          </Button>
          <Button 
            data-testid="add-category-btn"
            onClick={() => {
              setEditingCategory(null);
              setShowForm(true);
            }}
            className="btn-primary"
          >
            <Plus size={18} className="ml-2" />
            {t('categories.addCategory')}
          </Button>
        </div>
      </div>

      {/* Stats Card */}
      <div className="glass rounded-xl p-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-lg bg-violet-500/20 flex items-center justify-center">
            <Tags size={24} className="text-violet-400" />
          </div>
          <div>
            <h4 className="font-semibold">{isArabic ? 'فئات تيليجرام الرسمية' : 'Official Telegram Categories'}</h4>
            <p className="text-sm text-neutral-500">
              {isArabic 
                ? `${categories.length} فئة مضافة من أصل ${TELEGRAM_CATEGORIES.length} فئة متاحة`
                : `${categories.length} added of ${TELEGRAM_CATEGORIES.length} available categories`
              }
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-3xl font-bold text-violet-400">{categories.length}</p>
          <p className="text-xs text-neutral-500">/ {TELEGRAM_CATEGORIES.length}</p>
        </div>
      </div>

      {/* Info Card */}
      <div className="glass rounded-xl p-4 flex items-center gap-4 border-violet-500/30">
        <div className="w-12 h-12 rounded-lg bg-violet-500/20 flex items-center justify-center">
          <Sparkles size={24} className="text-violet-400" />
        </div>
        <div>
          <h4 className="font-semibold">{t('categories.aiClassification')}</h4>
          <p className="text-sm text-neutral-500">
            {t('categories.aiClassificationDesc')}
          </p>
        </div>
      </div>

      {/* Categories Grid */}
      {categories.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map(category => (
            <CategoryCard
              key={category.id}
              category={category}
              onEdit={handleEdit}
              onDelete={handleDelete}
              t={t}
              i18n={i18n}
            />
          ))}
        </div>
      ) : (
        <div className="empty-state glass rounded-xl">
          <Tags className="empty-state-icon mx-auto text-neutral-600" />
          <h3 className="text-lg font-semibold text-neutral-400">{t('categories.noCategories')}</h3>
          <p className="text-neutral-500 mt-2">{t('categories.addCategoriesToClassify')}</p>
          <Button 
            onClick={() => setShowForm(true)}
            className="btn-primary mt-4"
          >
            <Plus size={18} className="ml-2" />
            {t('categories.addCategory')}
          </Button>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <CategoryForm
          category={editingCategory}
          existingCategories={categories}
          onSave={handleSave}
          onClose={() => {
            setShowForm(false);
            setEditingCategory(null);
          }}
          t={t}
          i18n={i18n}
        />
      )}
    </div>
  );
}
